import { getLoggerInstance } from './registries';
import { LogLevel, LogOptions, LogMetadata, LogManagerConfig } from './types';

export class LogManager {
  private initialized = false;
  private config: LogManagerConfig | null = null;
  private runtime: 'browser' | 'node' | null = null;

  private static readonly MAX_SERIALIZED_LENGTH = 4000;

  init(options: LogManagerConfig): void {
    if (this.initialized) {
      return;
    }

    const isClientSide = typeof window !== 'undefined';

    this.runtime = isClientSide ? 'browser' : 'node';
    this.config = options;
    this.initialized = true;
  }

  private baseMetadata(extra?: LogMetadata): LogMetadata {
    return {
      appName: this.config?.appName,
      environment: this.config?.environment,
      runtime: this.runtime,
      ...(extra ?? {}),
    };
  }

  // NOTE: Normalize and cut-off the payload if it exceeds the maximum serialized length.
  private normalizePayload(data: any): any {
    if (data == null) {
      return undefined;
    }

    if (typeof data === 'string') {
      if (data.length <= LogManager.MAX_SERIALIZED_LENGTH) {
        return data;
      }

      return `${data.slice(0, LogManager.MAX_SERIALIZED_LENGTH)}...[truncated]`;
    }

    try {
      const json = JSON.stringify(data);
      if (json.length <= LogManager.MAX_SERIALIZED_LENGTH) {
        return JSON.parse(json);
      }
      return {
        truncated: true,
        preview: json.slice(0, LogManager.MAX_SERIALIZED_LENGTH),
      };
    } catch {
      return '[Unserializable payload]';
    }
  }

  private buildOption(options?: LogOptions): LogOptions | undefined {
    if (!options) return undefined;

    const built: LogOptions = {
      data: this.normalizePayload(options.data),
      error: options.error,
      metadata: this.baseMetadata(options.metadata),
    };

    // remove undefined & empty object
    const cleaned = Object.fromEntries(
      Object.entries(built).filter(([, v]) => {
        if (v === undefined) return false;
        if (typeof v === 'object' && v && Object.keys(v).length === 0) return false;
        return true;
      }),
    );

    return Object.keys(cleaned).length > 0 ? (cleaned as LogOptions) : undefined;
  }

  buildLog(level: LogLevel, message: string, options?: LogOptions): void {
    const builtOption = this.buildOption(options);

    const isProduction = this.config?.environment === 'production';
    if (isProduction) {
      // NOTE: Will implement 3rd logger for production, currently not use log in production because cause main thread blocking
      // const loggerInstance = getLoggerInstance();
      // if (!loggerInstance) {
      //   return;
      // }

      // loggerInstance.log(level, message, builtOption);

      return;
    }

    switch (level) {
      case 'info': {
        builtOption
          ? console.info(`[INFO] ${message}`, builtOption)
          : console.info(`[INFO] ${message}`);

        return;
      }
      case 'warn': {
        builtOption
          ? console.warn(`[WARN] ${message}`, builtOption)
          : console.warn(`[WARN] ${message}`);

        return;
      }
      case 'error': {
        builtOption
          ? console.error(`[ERROR] ${message}`, builtOption)
          : console.error(`[ERROR] ${message}`);

        return;
      }
      default: {
        builtOption
          ? console.log(`[LOG] ${message}`, builtOption)
          : console.log(`[LOG] ${message}`);

        return;
      }
    }
  }

  info(message: string, options?: LogOptions): void {
    this.buildLog('info', message, options);
  }

  warn(message: string, options?: LogOptions): void {
    this.buildLog('warn', message, options);
  }

  error(message: string, options?: LogOptions): void {
    this.buildLog('error', message, options);
  }
}

export const logManager = new LogManager();
